import React, { useState, useEffect } from 'react';
import { FaDownload, FaExternalLinkAlt } from 'react-icons/fa';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { getPdfUrl } from '../utils/imageUtils';

// Import the styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const PDFViewer = ({ pdfUrl, title = "PDF Document", showControls = true, className = "" }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Get the properly formatted PDF URL
  const fullPdfUrl = getPdfUrl(pdfUrl);

  // Create default layout plugin
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fullPdfUrl;
    link.download = title || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(fullPdfUrl, '_blank');
  };

  if (!pdfUrl) {
    return null;
  }

  const containerClasses = `pdf-viewer-container ${className}`;
  const iframeClasses = "w-100 border rounded";

  return (
    <div className={containerClasses}>
      {showControls && (
        <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
          <div className="d-flex align-items-center">
            <h5 className="mb-0 me-3">
              <FaDownload className="me-2" />
              {title}
            </h5>
            {isLoading && (
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading PDF...</span>
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={handleDownload}
              title="Download PDF"
            >
              <FaDownload />
            </button>
            <button 
              className="btn btn-outline-secondary btn-sm"
              onClick={handleOpenInNewTab}
              title="Open in new tab"
            >
              <FaExternalLinkAlt />
            </button>
          </div>
        </div>
      )}

      <div className="flex-grow-1 position-relative">
        {hasError ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
            <div className="text-danger mb-3">
              <FaDownload size={48} />
            </div>
            <h5 className="text-danger">Failed to load PDF</h5>
            <p className="text-muted mb-3">
              The PDF document could not be displayed. You can still download it using the button above.
            </p>
            <button 
              className="btn btn-primary"
              onClick={handleDownload}
            >
              <FaDownload className="me-2" />
              Download PDF
            </button>
          </div>
         ) : (
           <div className="pdf-viewer-wrapper" style={{ height: isMobile ? '500px' : '600px' }}>
             <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
               <Viewer
                 fileUrl={fullPdfUrl}
                 plugins={[defaultLayoutPluginInstance]}
                 onDocumentLoad={handleLoad}
                 onLoadError={handleError}
               />
             </Worker>
           </div>
         )}
      </div>

      <style jsx>{`
        .pdf-viewer-container {
          background: #f8f9fa;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        
        .pdf-viewer-wrapper {
          width: 100%;
          height: 100%;
          background: white;
        }
        
        .pdf-viewer-wrapper .rpv-core__viewer {
          border: none !important;
        }
        
        @media (max-width: 768px) {
          .pdf-viewer-container {
            margin: 0 -15px;
            border-radius: 0;
          }
          
          .pdf-viewer-wrapper {
            height: 500px !important;
            min-height: 400px !important;
          }
        }
        
        @media (max-width: 480px) {
          .pdf-viewer-wrapper {
            height: 450px !important;
            min-height: 350px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PDFViewer;
